import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Confetti, CheckCircle, ArrowLeft, Sparkle } from "@phosphor-icons/react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { SquarePayment } from "./SquarePayment";
import { ReceiptOptions } from "./ReceiptOptions";
import { PHONE, PHONE_RAW } from "../data";
import { squareEnabled } from "../lib/square";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EVENT_TYPES = ["Wedding", "Birthday Party", "Graduation Party", "Anniversary", "Corporate Event", "Other Celebration"];
const EMPTY = { event_type: "", name: "", phone: "", email: "", date: "", time: "", location: "", guests: "", details: "" };
const inputCls = "border-2 border-ink rounded-none h-12 bg-white text-ink focus-visible:ring-2 focus-visible:ring-brandred";

export const Events = () => {
  const [f, setF] = useState(EMPTY);
  const [step, setStep] = useState("form"); // form | deposit | done_pay | done_enquiry
  const [fare, setFare] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));
  const reset = () => { setF(EMPTY); setFare(null); setStep("form"); };

  const validate = () => {
    if (!f.event_type || !f.name.trim() || !f.phone.trim()) {
      toast.error("Please add the event type, your name and phone.");
      return false;
    }
    return true;
  };

  const startDeposit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/events/deposit`, { ...f, email: f.email || null });
      setFare(data);
      setStep("deposit");
    } catch {
      toast.error("Couldn't start the booking — please call us.");
    } finally { setLoading(false); }
  };

  const sendEnquiry = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, {
        name: f.name, phone: f.phone, email: f.email || undefined,
        service_type: `Event / Party Enquiry — ${f.event_type}`,
        pickup: f.location, date: f.date, time: f.time,
        notes: `EVENT ENQUIRY · ${f.guests || ""} · ${f.details || ""}`,
      });
      setStep("done_enquiry");
      toast.success("Enquiry sent! We'll be in touch.");
    } catch {
      toast.error("Couldn't send — please call us.");
    } finally { setLoading(false); }
  };

  const payDeposit = async (sourceId) => {
    setLoading(true);
    try {
      await axios.post(`${API}/payments/square`, {
        fare_id: fare.fare_id, source_id: sourceId,
        idempotency_key: (crypto.randomUUID?.() || String(Date.now())),
      });
      setStep("done_pay");
      toast.success("Deposit received — your date is secured!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment failed. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <section id="events" data-testid="events-section" className="py-20 sm:py-28 bg-paper">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[0.95fr_1.05fr] gap-12 lg:gap-16 items-start">
        <div className="lg:sticky lg:top-28">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brandred mb-3 flex items-center gap-2">
            <Confetti weight="fill" size={16} /> Events & parties
          </p>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] text-ink">
            Weddings, birthdays<br /><span className="text-brandred">& big nights.</span>
          </h2>
          <p className="mt-5 text-ink/70 leading-relaxed max-w-md">
            Book DS CABS for your wedding, birthday, graduation or any celebration — reliable rides and delivery for you and your guests, all night long.
          </p>
          <div className="mt-7 bg-sun border-2 border-ink p-5 max-w-md shadow-hard-sm">
            <p className="flex items-center gap-2 font-display font-extrabold text-xl text-ink">
              <Sparkle weight="fill" size={20} /> $100 booking deposit
            </p>
            <p className="mt-2 text-ink text-sm leading-relaxed">
              Secures your date and is <strong>credited in full toward your final bill</strong>. It's refundable once your total (before tax) goes over $100.
            </p>
          </div>
        </div>

        {step === "form" && (
          <div data-testid="events-form" className="bg-white border-2 border-ink p-6 sm:p-8 shadow-hard">
            <h3 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight text-ink mb-5">Book your event</h3>
            <div className="space-y-4">
              <Select value={f.event_type} onValueChange={set("event_type")}>
                <SelectTrigger data-testid="event-type" className={inputCls}><SelectValue placeholder="What are you celebrating?" /></SelectTrigger>
                <SelectContent className="bg-white border-2 border-ink rounded-none">
                  {EVENT_TYPES.map((t) => (<SelectItem key={t} value={t} data-testid={`event-type-${t}`}>{t}</SelectItem>))}
                </SelectContent>
              </Select>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input data-testid="event-name" value={f.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                <Input data-testid="event-phone" value={f.phone} onChange={set("phone")} placeholder="Phone" className={inputCls} />
              </div>
              <Input data-testid="event-email" type="email" value={f.email} onChange={set("email")} placeholder="Email (for your receipt)" className={inputCls} />
              <div className="grid sm:grid-cols-2 gap-4">
                <Input data-testid="event-date" type="date" value={f.date} onChange={set("date")} className={inputCls} />
                <Input data-testid="event-time" type="time" value={f.time} onChange={set("time")} className={inputCls} />
              </div>
              <Input data-testid="event-location" value={f.location} onChange={set("location")} placeholder="Venue / location" className={inputCls} />
              <Input data-testid="event-guests" value={f.guests} onChange={set("guests")} placeholder="Approx. guests / vehicles needed" className={inputCls} />
              <Textarea data-testid="event-details" value={f.details} onChange={set("details")} rows={2} placeholder="Tell us about your event…" className="border-2 border-ink rounded-none focus-visible:ring-2 focus-visible:ring-brandred" />
            </div>
            <button data-testid="event-deposit-btn" type="button" onClick={startDeposit} disabled={loading}
              className="mt-6 w-full bg-brandred text-white font-bold text-base py-4 border-2 border-ink shadow-hard-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all duration-150 disabled:opacity-60">
              {loading ? "Please wait…" : "Pay $100 deposit & secure date"}
            </button>
            <button data-testid="event-enquiry-btn" type="button" onClick={sendEnquiry} disabled={loading}
              className="mt-3 w-full bg-white text-ink font-bold text-base py-3.5 border-2 border-ink hover:bg-sun transition-colors disabled:opacity-60">
              Just send an enquiry
            </button>
          </div>
        )}

        {step === "deposit" && fare && (
          <div data-testid="events-deposit" className="bg-white border-2 border-ink p-6 sm:p-8 shadow-hard">
            <button data-testid="event-back" onClick={() => setStep("form")} className="flex items-center gap-1 text-xs font-bold text-ink/60 hover:text-ink mb-3"><ArrowLeft size={14} /> Back</button>
            <div className="bg-ink text-white border-2 border-ink p-5">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-sun text-center">Booking deposit</p>
              <div className="mt-3 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-white/70">Deposit</span><span className="font-semibold">${Number(fare.price).toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-white/70">Tax ({fare.tax_percent}%)</span><span className="font-semibold">${Number(fare.tax).toFixed(2)}</span></div>
              </div>
              <div className="border-t-2 border-white/20 mt-3 pt-3 flex items-end justify-between">
                <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/70">Total (CAD)</span>
                <span data-testid="event-deposit-total" className="font-display font-extrabold text-4xl tracking-tighter">${Number(fare.total).toFixed(2)}</span>
              </div>
            </div>
            <div className="mt-4">
              {squareEnabled()
                ? <SquarePayment amount={fare.total} onToken={payDeposit} busy={loading} />
                : <p className="text-sm text-ink/60">Card payments are being set up. Please call {PHONE} to pay your deposit.</p>}
            </div>
          </div>
        )}

        {step === "done_pay" && (
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            data-testid="events-done-pay" className="bg-white border-2 border-ink p-8 sm:p-10 shadow-hard flex flex-col items-start">
            <CheckCircle weight="fill" size={56} className="text-brandblue" />
            <h3 className="font-display font-extrabold text-3xl tracking-tight mt-4 text-ink">Your date is secured!</h3>
            <p className="mt-3 text-ink/70 leading-relaxed">Thanks {f.name.split(" ")[0]} — we've received your ${Number(fare?.total).toFixed(2)} deposit. We'll confirm the details soon.</p>
            <ReceiptOptions fare={fare} defaultEmail={f.email} defaultPhone={f.phone} />
            <button data-testid="events-reset" onClick={reset} className="mt-5 bg-ink text-white font-bold px-6 py-3 border-2 border-ink hover:bg-sun hover:text-ink transition-colors">Book another</button>
          </motion.div>
        )}

        {step === "done_enquiry" && (
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            data-testid="events-done-enquiry" className="bg-white border-2 border-ink p-8 sm:p-10 shadow-hard flex flex-col items-start">
            <CheckCircle weight="fill" size={56} className="text-brandblue" />
            <h3 className="font-display font-extrabold text-3xl tracking-tight mt-4 text-ink">Enquiry sent!</h3>
            <p className="mt-3 text-ink/70 leading-relaxed">Thanks {f.name.split(" ")[0]} — we'll call or text you to plan your event.</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <a data-testid="event-call" href={`tel:${PHONE_RAW}`} className="bg-sun text-ink font-bold px-5 py-2.5 border-2 border-ink hover:brightness-95 transition-all">Call {PHONE}</a>
              <button data-testid="events-enquiry-again" onClick={reset} className="bg-ink text-white font-bold px-5 py-2.5 border-2 border-ink hover:bg-sun hover:text-ink transition-colors">Start over</button>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};
